import { inject, LOCALE_ID, Service } from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  PRIMARY_OUTLET,
  Router,
  Event as RouterEvent,
  Routes,
  UrlSegment,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { LOCALIZED_APP_ROUTES } from '../localized-app-routes.token';
import { LocaleDeploymentService } from './locale-deployment.service';

@Service()
export class LocalizedRouteService {
  private readonly router = inject(Router);
  private readonly localeId = inject(LOCALE_ID);
  private readonly localeDeployment = inject(LocaleDeploymentService);
  private readonly localizedRoutes = inject(LOCALIZED_APP_ROUTES);

  public getLocalizedRoute(...routeRefs: Record<string, string>[]): string {
    return routeRefs.map((routeRef) => routeRef[this.localeId]).join('/');
  }

  /**
   * Navigate to the same route in a different locale.
   *
   * A locale is a separate bundle, so this leaves the Angular router behind and
   * reloads the page — unless the app is served without a locale prefix, which
   * means the single-locale dev-server, where the target bundle does not exist
   * and the URL is logged instead.
   *
   * A switch requested while the app is still starting up waits for the first
   * navigation, because only then is there a page to translate.
   */
  public async navigateToLocale(targetLocale: string): Promise<void> {
    await this.firstNavigationSettled();
    const targetUrl = this.urlForLocale(targetLocale);

    if (this.localeDeployment.isLanguagePrefixed) {
      this.redirectTo(targetUrl);
    } else {
      console.log(`should navigate to ${targetUrl}`);
    }
  }

  /**
   * The current page in `targetLocale`, or that locale's root when the page has
   * no counterpart there.
   */
  private urlForLocale(targetLocale: string): string {
    const translatedPath = this.translateCurrentPath(targetLocale);

    if (translatedPath === null) {
      console.warn(`No route found for ${this.router.url} in locale: ${targetLocale}`);
      return this.localeDeployment.urlForLanguage(targetLocale);
    }

    return `${this.localeDeployment.urlForLanguage(targetLocale, translatedPath)}${this.searchAndHash}`;
  }

  /**
   * The query string and the fragment of the current URL, carried over to the target locale
   * so a switch keeps the state a page holds there — the selected tabs and filters of a
   * list, an anchor within a page.
   */
  protected get searchAndHash(): string {
    return `${window.location.search}${window.location.hash}`;
  }

  /**
   * Resolves once the router has a state to translate: immediately after the first navigation,
   * otherwise when it ends. A navigation that fails, or is refused without a redirect taking its
   * place, settles it too; the page then has no counterpart and the switch goes to the root.
   */
  private async firstNavigationSettled(): Promise<void> {
    if (this.router.navigated) {
      return;
    }

    await firstValueFrom(this.router.events.pipe(filter((event) => this.isFinalNavigationEvent(event))));
  }

  private isFinalNavigationEvent(event: RouterEvent): boolean {
    if (event instanceof NavigationEnd || event instanceof NavigationError) {
      return true;
    }

    return (
      event instanceof NavigationCancel &&
      event.code !== NavigationCancellationCode.Redirect &&
      event.code !== NavigationCancellationCode.SupersededByNewNavigation
    );
  }

  /**
   * The path of the page on show, translated into `targetLocale`, segment by segment.
   *
   * Every locale's routes come from the same factory, so the two trees have the same shape: the
   * route active at a position in the current tree is translated by the route at that position in
   * the target tree, which must carry the same `routeId`. Literal parts of its path take the
   * target's wording, `:param` parts keep the value in the URL, and every segment keeps its matrix
   * parameters. The values themselves are carried unchanged, so a matrix or query value must mean
   * the same in every locale.
   *
   * @param {string} targetLocale - The locale to translate into
   * @returns {string | null} The translated path without a leading slash, or `null` when some level
   *   of the page has no counterpart in the target tree
   */
  private translateCurrentPath(targetLocale: string): string | null {
    let routeSnapshot = this.router.routerState.snapshot.root.firstChild;
    if (!routeSnapshot) {
      return null;
    }

    let activeSiblings: Routes = this.router.config;
    let currentLocaleSiblings: Routes = this.localizedRoutes(this.localeId);
    let targetLocaleSiblings: Routes = this.localizedRoutes(targetLocale);
    const translatedSegments: UrlSegment[] = [];

    while (routeSnapshot) {
      const activeRoute = routeSnapshot.routeConfig;
      const position = activeRoute ? activeSiblings.indexOf(activeRoute) : -1;
      const currentLocaleRoute = currentLocaleSiblings[position];
      const targetLocaleRoute = targetLocaleSiblings[position];

      // A tree shifted on either side — a route the factory does not build at this position in the
      // current locale, or one the target locale has in addition — would pair with the wrong
      // translation. `buildRouteShape` lets a spec pin the shapes equal before this ever fires.
      if (
        !activeRoute ||
        !currentLocaleRoute ||
        !targetLocaleRoute ||
        currentLocaleRoute.path !== activeRoute.path ||
        targetLocaleRoute.data?.['routeId'] !== activeRoute.data?.['routeId']
      ) {
        return null;
      }

      const levelSegments = this.translateSegments(routeSnapshot.url, activeRoute.path ?? '', targetLocaleRoute.path ?? '');
      if (!levelSegments) {
        return null;
      }

      translatedSegments.push(...levelSegments);
      activeSiblings = activeRoute.children ?? [];
      currentLocaleSiblings = currentLocaleRoute.children ?? [];
      targetLocaleSiblings = targetLocaleRoute.children ?? [];
      routeSnapshot = routeSnapshot.firstChild;
    }

    const translatedTree = new UrlTree(new UrlSegmentGroup([], { [PRIMARY_OUTLET]: new UrlSegmentGroup(translatedSegments, {}) }));
    return this.router.serializeUrl(translatedTree).replace(/^\//, '');
  }

  /**
   * One level's URL segments, with its path's literal parts swapped for the target's.
   *
   * @param {UrlSegment[]} segments - The segments the level consumed
   * @param {string} activePath - The level's path in the current locale
   * @param {string} targetPath - The same level's path in the target locale
   * @returns {UrlSegment[] | null} The translated segments, or `null` when the two paths do not
   *   have the same parts
   */
  private translateSegments(segments: UrlSegment[], activePath: string, targetPath: string): UrlSegment[] | null {
    const activeParts = this.splitPath(activePath);
    const targetParts = this.splitPath(targetPath);
    const partsCorrespond =
      activeParts.length === segments.length &&
      targetParts.length === segments.length &&
      targetParts.every((targetPart, position) => this.isParameter(targetPart) === this.isParameter(activeParts[position]));

    if (!partsCorrespond) {
      return null;
    }

    return targetParts.map(
      (targetPart, position) =>
        new UrlSegment(this.isParameter(targetPart) ? segments[position].path : targetPart, segments[position].parameters),
    );
  }

  private splitPath(path: string): string[] {
    return path === '' ? [] : path.split('/');
  }

  private isParameter(pathPart: string): boolean {
    return pathPart.startsWith(':');
  }

  /**
   * Performs a full-page redirect. Kept as a seam because we intentionally bypass
   * the Angular router here (target locale is served by a separate build).
   */
  private redirectTo(url: string): void {
    window.location.href = url;
  }
}
